import React, { ReactNode, useEffect, useState } from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { CheckboxMenuItem, ListItem, OptionLabel } from '@producer/ui-kit';

import { Tooltip } from '../tooltip/Tooltip';

import styles from './drilldown-menu.module.scss';
type Item = {
  menuKey?: string;
  onSelect: (event: Event) => void;
  title?: ReactNode;
  icon?: ReactNode;
  type?: 'danger' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
  tooltipText?: string;
  subItems?: DrilldownMenuProps['items'];
  isChecked?: never;
  onCheckedChange?: never;
  customComponent?: React.ReactElement;
};

type CheckboxItem = Omit<Item, 'onSelect' | 'isChecked' | 'onCheckedChange'> & {
  isChecked?: boolean;
  onCheckedChange?: (isChecked: boolean) => void;
  onSelect?: (event: Event) => void;
};

type DrilldownMenuProps = {
  items: Array<Item | CheckboxItem>;
  align?: 'start' | 'end';
  size?: 'small' | 'medium' | 'large' | 'free-size';
  trigger?: ReactNode;
  itemComponent?: React.ReactElement;
  tooltipText?: string;
  modal?: boolean;
  onOpenChange?: (value: boolean) => void;
  portalContainer?: HTMLDivElement;
  side?: 'bottom' | 'top';
  alignOffset?: number;
  className?: string;
  disabled?: boolean;
  defaultMenuPath: number[];
};

export const DrilldownMenu: React.FC<DrilldownMenuProps> = ({
  items,
  size,
  trigger,
  itemComponent: optionComponent,
  align = 'end',
  alignOffset,
  modal = false,
  onOpenChange,
  portalContainer,
  side,
  className,
  disabled,
  defaultMenuPath,
}) => {
  const [currentOptions, setCurrentOptions] = useState(items);
  const [menuPath, setMenuPath] = useState<number[]>(defaultMenuPath);

  const hasIcon = currentOptions.some((item) => !!item.icon || !!item.onCheckedChange);
  const hasIconAndCheck = currentOptions.some((item) => !!item.icon && !!item.onCheckedChange);

  useEffect(() => {
    if (menuPath.length === 0) {
      setCurrentOptions(items);
    } else {
      const newOptions = menuPath.reduce((acc, index) => {
        return acc[index]?.subItems || [];
      }, items);

      setCurrentOptions(newOptions);
    }
  }, [items, menuPath]);

  useEffect(() => {
    setMenuPath(defaultMenuPath);
  }, [defaultMenuPath]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setMenuPath([]);
    }

    onOpenChange?.(isOpen);
  };

  const renderItem = (item: Item | CheckboxItem, index: number, hasIcon?: boolean) => {
    if (item.hidden) {
      return;
    }

    if (optionComponent && React.isValidElement(optionComponent)) {
      return React.cloneElement(optionComponent, { ...item });
    }

    if (hasIconAndCheck) {
      return (
        <CheckboxMenuItem
          key={`option-${index}`}
          checked={item.isChecked}
          onCheckedChange={item.onCheckedChange}
          onSelect={item.onSelect}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.onCheckedChange) {
      return (
        <CheckboxMenuItem
          key={`option-${item.title}-${index}`}
          onSelect={item.onSelect}
          onCheckedChange={(isChecked) => {
            item.onCheckedChange?.(isChecked);
          }}
          checked={item.isChecked}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.customComponent) {
      return React.cloneElement(item.customComponent, { ...item });
    }

    return (
      <ListItem
        key={`option-${index}`}
        id={`option-${index}`}
        disabled={item.disabled}
        onSelect={(e) => {
          if (item.subItems?.length) {
            e.preventDefault();
            setMenuPath((prev) => [...prev, index]);
          }
          item.onSelect?.(e);
        }}
        data-type={item.type}
      >
        <OptionLabel>
          {hasIcon && item.icon}

          <span>{item.title}</span>
        </OptionLabel>
      </ListItem>
    );
  };

  return (
    <RadixMenu.Root modal={modal} onOpenChange={handleOpenChange}>
      <RadixMenu.Trigger onClick={(e) => e.stopPropagation()} disabled={disabled} asChild>
        {trigger || (
          <button className={styles['trigger']} data-size={size}>
            ⋮
          </button>
        )}
      </RadixMenu.Trigger>

      <RadixMenu.Portal {...(portalContainer && { container: portalContainer })}>
        <RadixMenu.Content
          className={styles['content']}
          side={side || 'bottom'}
          align={align}
          sideOffset={6}
          alignOffset={alignOffset}
          asChild
        >
          <div
            className={`${styles['menu-modal']} ${className || ''}`}
            data-size={size}
            onClick={(e) => e.stopPropagation()}
          >
            {currentOptions.map((item, index) => {
              return (
                <Tooltip
                  text={item.tooltipText || 'Action not allowed'}
                  key={`tip-${index}`}
                  disabled={!item.disabled || !item.tooltipText}
                >
                  {renderItem(item, index, hasIcon)}
                </Tooltip>
              );
            })}
          </div>
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
};
